import {
  Button,
  CheckboxOptionType,
  Col,
  Grid,
  Image,
  Pagination,
  RadioChangeEvent,
  Row,
} from "antd";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { ContentWrapper } from "../../layouts/components";

import CaretRight from "../../assets/images/icons/CaretRight";
import { NoData, Title } from "../../components";
import CustomRadioButton from "../../components/CustomRadioButton";
import { ALL_KEY } from "../../components/CustomRadioButton/enumerations/enumerations";
import { P1, P2, P3 } from "../../components/NormalFonts";
import { useGetNews, useGetNewsCategory } from "../../hooks/News/news.hooks";
import useGetTimezone from "../../hooks/Timezone/timezone.hooks";

import { useNavigate } from "react-router-dom";
import {
  NewsCategoryResponse,
  NewsProps,
  NewsResponse,
} from "../../hooks/News/data";
import { scrollToTop } from "../../plugins/utils";
import styles from "./styles.module.scss";

const News: React.FC<NewsProps> = () => {
  const navigate = useNavigate();
  const PAGE_SIZE = 10;
  const { useBreakpoint } = Grid;
  const { toLocalTimezone, formatSmartDate } = useGetTimezone();
  const screens = useBreakpoint();
  const { t } = useTranslation();
  const [selectedCategory, setSelectedCategory] = useState<string | number>(
    ALL_KEY
  );
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [totalPages, setTotalPages] = useState<number>(0);

  const { data: news } = useGetNews(
    {
      page: currentPage,
      page_size: PAGE_SIZE,
      category_id: selectedCategory === ALL_KEY ? undefined : selectedCategory,
    },
    {}
  );

  const { data: newsCategory, isLoading: loadingCategory } = useGetNewsCategory(
    {}
  );

  const [options, setOptions] = useState<
    (string | number | CheckboxOptionType<any>)[]
  >([]);

  const onPageChange = (page: number, pageSize: number) => {
    console.log(page);
    setCurrentPage(page);
    scrollToTop("main-layout");
  };

  useEffect(() => {
    if (!news) return;
    setTotalPages(news.total_pages);
    console.debug("news", news);
  }, [news]);

  const onChange = (e: RadioChangeEvent) => {
    setSelectedCategory(e.target.value);
  };

  const handlePrevPage = () => {
    if (currentPage > 1) setCurrentPage((prevState) => prevState - 1);
  };
  const handleNextPage = () => {
    if (currentPage < totalPages) setCurrentPage((prevState) => prevState + 1);
  };

  useEffect(() => {
    if (!newsCategory) return;
    setOptions(
      newsCategory.map((news: NewsCategoryResponse) => {
        return {
          value: news.id,
          label: t([`${news.name}`]),
        };
      })
    );
  }, [newsCategory]);

  const handleOpenDetail = (id: number) => {
    navigate(`/news/detail/${id}`);
  };

  return (
    <ContentWrapper>
      <Title title={t("news.title")} subtitle={t("news.subtitle")} />
      <CustomRadioButton
        items={options}
        selectedValue={selectedCategory}
        onValueChange={onChange}
        loading={loadingCategory}
      />
      {news?.article_list?.length <= 0 ? (
        <NoData
          title={t("news.nodata.title")}
          subtitle={t("news.nodata.subtitle")}
          offsetTop={80}
        />
      ) : (
        <div className={styles.newsContainer}>
          <Row
            style={{ width: "100%" }}
            gutter={screens.sm ? [22, 22] : [0, 22]}
          >
            {news?.article_list?.map((article: NewsResponse, index: number) => {
              return (
                <Col span={screens.xl ? 6 : screens.sm ? 8 : 24} key={index}>
                  <div
                    onClick={() => handleOpenDetail(article.id)}
                    className={styles.articleItemContainer}
                  >
                    <Image
                      preview={false}
                      src={article.image_url}
                      width={"100%"}
                      height={176}
                      style={{ objectFit: "cover" }}
                    />
                    <div className={styles.contentWrapper}>
                      <P1 weight="medium" color="var(--text-content-1)">
                        {article.title}
                      </P1>
                      <div className={styles.wrapper}>
                        <P2 color="var(--text-content-2)">
                          {article.description}
                        </P2>
                      </div>
                      <P3 color="var(--text-content-3">
                        {formatSmartDate(toLocalTimezone(article.created_at))}
                      </P3>
                    </div>
                  </div>
                </Col>
              );
            })}
          </Row>
          {screens.sm ? (
            <Pagination
              className={styles.pagination}
              current={currentPage}
              onChange={onPageChange}
              showSizeChanger={false}
              total={news?.total}
            />
          ) : (
            <div className={styles.mobilePaging}>
              <Button
                size="small"
                disabled={currentPage <= 1}
                onClick={handlePrevPage}
                iconPosition={"start"}
                icon={
                  <CaretRight
                    style={{
                      fontSize: 24,
                      color: "var(--primary)",
                      transform: "rotate(180deg)",
                    }}
                  />
                }
                type={"link"}
              >
                {t("normal.prevpage")}
              </Button>
              <Button
                size="small"
                disabled={currentPage >= totalPages}
                onClick={handleNextPage}
                iconPosition={"end"}
                icon={
                  <CaretRight
                    style={{ fontSize: 24, color: "var(--primary)" }}
                  />
                }
                type={"link"}
              >
                {t("normal.nextpage")}
              </Button>
            </div>
          )}
        </div>
      )}
    </ContentWrapper>
  );
};

export default News;
